/**
 * Os achados com destino (E153.F3, ADR-0104 §2.6): a situação da busca como o servidor a diz (a
 * análise não executada ou não conferida nunca se lê como "sem achados"), cada achado com o destino
 * e o grau, e se o reservado é composto nesta rodada.
 */
import { Badge, Blueprint } from '@/components/ui';
import type { AchadoData } from '@/lib/formalizacaoAvaliacao';
import type { AchadosDoResultadoData } from '@/lib/resultadoAuditado';

import { preencher, t, tf } from './textos';

const _GRAU = { alto: 'danger', medio: 'warning', baixo: 'neutral' } as const;

function Achado({ achado }: { achado: AchadoData }): JSX.Element {
  const { risco } = achado;
  return (
    <li
      className="space-y-sc-1 border-l-2 border-border pl-sc-3"
      data-testid="resultado-achado"
      data-natureza={achado.natureza}
      data-destinos={achado.destinos.join(' ')}
    >
      <p className="text-fg">
        {tf.naturezaDoAchado[achado.natureza]}{' '}
        {achado.destinos.map((d) => (
          <Badge key={d} variant="outline">
            {tf.destino[d]}
          </Badge>
        ))}{' '}
        {risco ? <Badge variant={_GRAU[risco.grau]}>{tf.grau[risco.grau]}</Badge> : null}
      </p>
      <p className="text-fg">{achado.fato}</p>
      <p className="text-caption text-fg-dim">{achado.fonte}</p>
    </li>
  );
}

function Par({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <>
      <dt className="text-fg-muted">{rotulo}</dt>
      <dd className="text-fg">{valor}</dd>
    </>
  );
}

export function AchadosDoResultado({ achados }: { achados: AchadosDoResultadoData }): JSX.Element {
  const a = t.achados;
  return (
    <Blueprint
      as="section"
      className="space-y-sc-4 p-sc-6"
      data-testid="resultado-achados"
      data-busca={achados.situacao_da_busca}
      aria-labelledby="resultado-achados-titulo"
    >
      <div>
        <h2 id="resultado-achados-titulo" className="text-body-lg text-fg">
          {a.titulo}
        </h2>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{a.subtitulo}</p>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-sc-3 gap-y-sc-1 text-body-sm">
        <Par rotulo={a.busca} valor={t.situacaoDaBusca[achados.situacao_da_busca]} />
        <Par rotulo={a.estado} valor={t.estadoDaAnalise[achados.estado]} />
        <Par rotulo={a.cobertura} valor={achados.cobertura} />
        <Par rotulo={a.criterio} valor={achados.criterio} />
      </dl>
      <p className="text-body-sm text-fg" data-testid="resultado-destinos">
        {preencher(a.laudoEssencial, { n: achados.laudo_essencial.length })} ·{' '}
        {preencher(a.riscosReservados, { n: achados.riscos_reservados.length })}
      </p>
      <p
        className="text-body-sm text-fg-muted"
        data-testid="resultado-reservado-a-compor"
        data-compor={achados.reservado_a_compor}
      >
        {achados.reservado_a_compor ? a.reservadoAgora : a.reservadoNao}
      </p>
      {achados.achados.length === 0 ? (
        <p className="text-body-sm text-fg-muted">{a.semAchados}</p>
      ) : (
        <ul className="space-y-sc-3 text-body-sm">
          {achados.achados.map((achado) => (
            <Achado key={achado.id} achado={achado} />
          ))}
        </ul>
      )}
    </Blueprint>
  );
}
